// src/ChatProvider.tsx
import React2, { useMemo as useMemo2 } from "react";
import {
  ChatClient,
  WidgetAuthProvider,
  PlatformAuthProvider,
  VisitorWidgetAuthProvider,
  buildChatPersistNamespace
} from "@dopomogai/chat-sdk";

// src/context/ChatContext.tsx
import { createContext, useContext } from "react";
var ChatContext = createContext(null);
var useChatClient = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChatClient must be used within ChatProvider");
  return ctx.client;
};

// src/context/RegistryContext.tsx
import { createContext as createContext2, useContext as useContext2, useMemo, useRef } from "react";
import { jsx } from "react/jsx-runtime";
var RegistryContext = createContext2(null);
var RegistryProvider = ({ children }) => {
  const registry = useRef({
    tools: /* @__PURE__ */ new Map(),
    components: {},
    toolResultRenderers: {}
  }).current;
  const value = useMemo(() => {
    const registerTool = (name, executor) => {
      registry.tools.set(name, executor);
    };
    const registerComponent = (name, component) => {
      registry.components[name] = component;
    };
    const registerToolResultRenderer = (name, component) => {
      registry.toolResultRenderers[name] = component;
    };
    return {
      registerTool,
      registerComponent,
      registerToolResultRenderer,
      registry
    };
  }, [registry]);
  return /* @__PURE__ */ jsx(RegistryContext.Provider, { value, children });
};
var useRegistry = () => {
  const ctx = useContext2(RegistryContext);
  if (!ctx) throw new Error("useRegistry must be used within a RegistryProvider (via ChatProvider)");
  return ctx;
};

// src/internal/ToolRunner.tsx
import { useEffect, useRef as useRef2 } from "react";
var ToolRunner = () => {
  const client = useChatClient();
  const { registry } = useRegistry();
  const runningTasks = useRef2(/* @__PURE__ */ new Set());
  const lastSignature = useRef2("");
  useEffect(() => {
    const unsubscribe = client.store.subscribe((state) => {
      const currentTasks = [];
      const sessionIds = Object.keys(state.sessions);
      for (const sessId of sessionIds) {
        const session = state.sessions[sessId];
        if (session.activeToolCalls.size > 0) {
          session.activeToolCalls.forEach((callId) => {
            currentTasks.push(`${sessId}:${callId}`);
          });
        }
      }
      const signature = currentTasks.sort().join("|");
      if (signature === lastSignature.current) return;
      lastSignature.current = signature;
      if (currentTasks.length === 0) return;
      currentTasks.forEach(async (taskKey) => {
        if (runningTasks.current.has(taskKey)) return;
        const [sessionId, callId] = taskKey.split(":");
        if (!sessionId || !callId) return;
        runningTasks.current.add(taskKey);
        let toolName;
        try {
          const currentState = client.store.getState();
          const session = currentState.sessions[sessionId];
          if (!session || !session.activeToolCalls.has(callId)) {
            runningTasks.current.delete(taskKey);
            return;
          }
          const message = session.messages.find((m) => m.parts.some((p) => p.type === "tool_call" && p.callId === callId));
          const part = message?.parts.find((p) => p.type === "tool_call" && p.callId === callId);
          if (!part || part.type !== "tool_call") {
            runningTasks.current.delete(taskKey);
            return;
          }
          toolName = part.name;
          const executor = registry.tools.get(toolName);
          if (!executor) {
            console.warn(`[ToolRunner] Tool '${toolName}' not found.`);
            await client.store.getState().addToolResult(sessionId, callId, { error: `Tool '${toolName}' not found in client registry.` }, toolName);
            return;
          }
          const runtimeConfig = currentState.config || {};
          const result = await executor(part.args, {
            conversationId: sessionId,
            toolCallId: callId,
            agentId: runtimeConfig.agentId,
            authMode: runtimeConfig.authMode,
            interactionMode: runtimeConfig.interactionMode,
            workspaceMode: runtimeConfig.workspaceMode,
            flowStep: runtimeConfig.flowStep,
            readinessSummary: runtimeConfig.readinessSummary,
            persistNamespace: runtimeConfig.persistNamespace
          });
          await client.submitToolResult(callId, result, toolName);
        } catch (e) {
          console.error("[ToolRunner] Error execution:", e);
          await client.store.getState().addToolResult(sessionId, callId, { error: e?.message || "Execution failed" }, toolName);
        } finally {
          runningTasks.current.delete(taskKey);
        }
      });
    });
    return () => unsubscribe();
  }, [client, registry]);
  return null;
};

// src/ChatProvider.tsx
import { setAuthHeaderProvider, setAuthRefresh } from "@dopomogai/api-client";
import { conversationService, widgetService, widgetsService } from "@dopomogai/api-client";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var RegistryInitializer = ({ tools, components, toolResultRenderers }) => {
  const { registerTool, registerComponent, registerToolResultRenderer } = useRegistry();
  React2.useEffect(() => {
    if (tools) Object.entries(tools).forEach(([name, fn]) => registerTool(name, fn));
    if (components) Object.entries(components).forEach(([name, cmp]) => registerComponent(name, cmp));
    if (toolResultRenderers) {
      Object.entries(toolResultRenderers).forEach(([name, cmp]) => registerToolResultRenderer(name, cmp));
    }
  }, [tools, components, toolResultRenderers, registerTool, registerComponent, registerToolResultRenderer]);
  return null;
};
function getOrCreateVisitorId(explicit) {
  if (explicit) return explicit;
  if (typeof window === "undefined") return crypto.randomUUID();
  const key = "dopomogai:visitor_id:v1";
  try {
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const id = crypto.randomUUID();
    localStorage.setItem(key, id);
    return id;
  } catch {
    return crypto.randomUUID();
  }
}
var ChatProvider = ({
  children,
  agentId,
  apiBaseUrl,
  authMode = "WIDGET",
  authToken,
  widgetVersionId,
  installationId,
  templateId,
  visitorId,
  refreshAuthToken,
  tools,
  components,
  toolResultRenderers,
  useV2Engine,
  allowClientTools,
  preset,
  features,
  compatApiFallbackEnabled = true,
  interactionMode,
  workspaceMode,
  flowStep,
  readinessSummary,
  persistNamespace,
  assistantProfileId,
  runtimeAgentId,
  subjectAgentId,
  subjectContextKey,
  onboardingSessionId,
  scanId,
  requestContext,
  turnPromptOverride
}) => {
  const resolvedPersistNamespace = useMemo2(
    () => buildChatPersistNamespace({
      namespace: persistNamespace,
      authMode,
      agentId,
      interactionMode,
      widgetVersionId,
      installationId,
      templateId
    }),
    [persistNamespace, authMode, agentId, interactionMode, widgetVersionId, installationId, templateId]
  );
  const client = useMemo2(() => {
    let authProvider;
    let refreshAuth;
    if (authMode === "PLATFORM") {
      authProvider = new PlatformAuthProvider(authToken || "");
      refreshAuth = async () => {
        if (!refreshAuthToken) throw new Error("No platform refreshAuthToken provided");
        const newToken = await refreshAuthToken();
        authProvider.onTokenRefresh?.(newToken);
        return newToken;
      };
    } else if (authMode === "VISITOR_WIDGET") {
      if (!widgetVersionId && !installationId && !templateId) {
        throw new Error("VISITOR_WIDGET requires widgetVersionId, installationId, or templateId");
      }
      const vid = getOrCreateVisitorId(visitorId);
      const authStorageId = widgetVersionId || templateId || `installation:${installationId}`;
      authProvider = new VisitorWidgetAuthProvider(authStorageId, authToken);
      refreshAuth = async () => {
        const current = await authProvider.getToken().catch(() => "");
        if (current && widgetVersionId) {
          const refreshed = await widgetService.refreshWat(widgetVersionId, current);
          if (!refreshed?.isError && refreshed?.token) {
            authProvider.onTokenRefresh?.(refreshed.token);
            return refreshed.token;
          }
        }
        const res = await widgetsService.initSession(apiBaseUrl, {
          installation_id: installationId,
          widget_id: widgetVersionId,
          template_id: templateId,
          visitor_id: vid
        });
        if (res?.isError) throw new Error(res.message || "Widget init-session failed");
        const wat = res.token;
        if (!wat) throw new Error("Widget init-session returned empty token");
        authProvider.onTokenRefresh?.(wat);
        return wat;
      };
      void (async () => {
        try {
          await authProvider.getToken();
        } catch {
          try {
            await refreshAuth?.();
          } catch {
          }
        }
      })();
    } else {
      authProvider = new WidgetAuthProvider(agentId, authToken);
      refreshAuth = async () => {
        const res = await conversationService.initTestChatSession(agentId);
        if (res?.isError) throw new Error(res.message || "WAT refresh failed");
        const wat = res.wat || res.token || res.access_token || "";
        if (!wat) throw new Error("WAT refresh returned empty token");
        authProvider.onTokenRefresh?.(wat);
        return wat;
      };
    }
    const nextClient = new ChatClient({
      agentId,
      apiBaseUrl,
      authMode,
      authProvider,
      useV2Engine: !!useV2Engine,
      refreshAuth,
      widgetVersionId: authMode === "VISITOR_WIDGET" ? widgetVersionId : void 0,
      installationId: authMode === "VISITOR_WIDGET" ? installationId : void 0,
      templateId: authMode === "VISITOR_WIDGET" ? templateId : void 0,
      allowClientTools: !!allowClientTools,
      preset,
      features,
      compatApiFallbackEnabled,
      interactionMode,
      workspaceMode,
      flowStep,
      readinessSummary,
      persistNamespace: resolvedPersistNamespace,
      assistantProfileId,
      runtimeAgentId,
      subjectAgentId,
      subjectContextKey,
      onboardingSessionId,
      scanId,
      requestContext,
      turnPromptOverride
    });
    setAuthHeaderProvider(() => nextClient.config.authProvider.getHeaders());
    setAuthRefresh(
      async () => {
        if (!refreshAuth) throw new Error("No refreshAuth available");
        const token = await refreshAuth();
        return token;
      },
      (newToken) => nextClient.config.authProvider.onTokenRefresh?.(newToken)
    );
    return nextClient;
  }, [
    agentId,
    apiBaseUrl,
    authMode,
    authToken,
    widgetVersionId,
    installationId,
    templateId,
    visitorId,
    refreshAuthToken,
    useV2Engine,
    allowClientTools,
    preset,
    features,
    compatApiFallbackEnabled,
    interactionMode,
    workspaceMode,
    flowStep,
    readinessSummary,
    resolvedPersistNamespace,
    assistantProfileId,
    runtimeAgentId,
    subjectAgentId,
    subjectContextKey,
    onboardingSessionId,
    scanId,
    requestContext,
    turnPromptOverride
  ]);
  return /* @__PURE__ */ jsxs(RegistryProvider, { children: [
    /* @__PURE__ */ jsx2(RegistryInitializer, { tools, components, toolResultRenderers }),
    /* @__PURE__ */ jsxs(ChatContext.Provider, { value: { client }, children: [
      allowClientTools ? /* @__PURE__ */ jsx2(ToolRunner, {}) : null,
      children
    ] })
  ] });
};

// src/hooks/useChat.ts
import { useStore } from "zustand";
import { useMemo as useMemo3 } from "react";
import { useShallow } from "zustand/react/shallow";
var EMPTY_MESSAGES = [];
var EMPTY_TOOL_CALLS = /* @__PURE__ */ new Set();
var chatStateSelector = (s) => {
  const activeId = s.activeConversationId;
  const session = activeId ? s.sessions[activeId] : null;
  return {
    activeId,
    messages: session?.messages || EMPTY_MESSAGES,
    status: session?.status || "IDLE",
    error: session?.error || null,
    errorCode: session?.errorCode || null,
    retryAfterSec: session?.retryAfterSec || null,
    isReasoning: session?.isReasoning || false,
    // FIX 1: Add activeToolCalls here so MessageList can see what's running
    activeToolCalls: session?.activeToolCalls || EMPTY_TOOL_CALLS
  };
};
var useChat = () => {
  const client = useChatClient();
  const {
    activeId,
    messages,
    status,
    error,
    errorCode,
    retryAfterSec,
    isReasoning,
    activeToolCalls
    // <--- Destructure it
  } = useStore(client.store, useShallow(chatStateSelector));
  const sendMessage = async (content, context) => {
    const targetId = activeId;
    await client.sendMessage(targetId, content, context);
  };
  const stop = () => {
    if (activeId) client.streamHandler.disconnect(activeId);
  };
  const reset = () => {
    client.store.getState().setActiveConversationId(null);
  };
  return useMemo3(() => ({
    messages,
    status,
    error,
    errorCode,
    retryAfterSec,
    isReasoning,
    activeToolCalls,
    // <--- Return it
    sendMessage,
    stop,
    reset,
    activeId,
    isLoading: status === "CONNECTING" || status === "CONNECTED",
    isConnected: status === "CONNECTED"
  }), [messages, status, error, errorCode, retryAfterSec, isReasoning, activeId, activeToolCalls]);
};

// src/hooks/useChatConfig.ts
import { useStore as useStore2 } from "zustand";
import { useShallow as useShallow2 } from "zustand/react/shallow";
var useChatConfig = () => {
  const client = useChatClient();
  return useStore2(client.store, useShallow2((s) => s.config));
};

// src/hooks/useGenerativeUI.ts
var useGenerativeUI = () => {
  const { registry } = useRegistry();
  const getComponent = (name) => {
    return registry.components[name] || null;
  };
  return { getComponent };
};

// src/hooks/useSessionDraft.ts
import { useStore as useStore3 } from "zustand";
import { useShallow as useShallow3 } from "zustand/react/shallow";
var EMPTY_ATTACHMENTS = [];
var useSessionDraft = () => {
  const client = useChatClient();
  const draftSelector = (s) => {
    const currentActiveId = s.activeConversationId;
    if (!currentActiveId || !s.sessions[currentActiveId]) {
      return {
        activeId: currentActiveId,
        text: "",
        attachments: EMPTY_ATTACHMENTS
      };
    }
    const sess = s.sessions[currentActiveId];
    return {
      activeId: currentActiveId,
      text: sess.draftText,
      attachments: sess.draftAttachments
    };
  };
  const { activeId, text, attachments } = useStore3(client.store, useShallow3(draftSelector));
  const setDraftText = (newText) => {
    if (activeId) {
      client.store.getState().setDraft(activeId, newText);
    }
  };
  const addAttachment = (file) => {
    if (activeId) {
      client.store.getState().addAttachment(activeId, file);
    }
  };
  const updateAttachment = (clientId, updates) => {
    if (activeId) {
      client.store.getState().updateAttachment(activeId, clientId, updates);
    }
  };
  const removeAttachment = (idOrFileId) => {
    if (activeId) {
      client.store.getState().removeAttachment(activeId, idOrFileId);
    }
  };
  const clearDraft = () => {
    if (activeId) {
      client.store.getState().clearDraft(activeId);
    }
  };
  return {
    text,
    attachments,
    setDraftText,
    addAttachment,
    updateAttachment,
    removeAttachment,
    clearDraft
  };
};

// src/hooks/useAvailableModels.ts
import { useEffect as useEffect2, useMemo as useMemo4, useState } from "react";
import { agentService, modelService, isApiError } from "@dopomogai/api-client";
function useAvailableModels(agentId) {
  const [models, setModels] = useState([]);
  const [agentDefaultDescriptor, setAgentDefaultDescriptor] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect2(() => {
    let cancelled = false;
    setLoading(true);
    modelService.getAvailableModels().then((res) => {
      if (cancelled) return;
      const list = Array.isArray(res) ? res : res?.models || [];
      const mapped = list.filter((m) => m && m.id).map((m) => ({
        id: m.id,
        name: m.name || m.label || m.id,
        provider: m.provider || "unknown",
        context_window_tokens: m.context_window_tokens ?? null
      }));
      setModels(mapped);
    }).catch((e) => {
      if (!cancelled) console.error("[useAvailableModels] failed", e);
    }).finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect2(() => {
    if (!agentId) {
      setAgentDefaultDescriptor("");
      return;
    }
    let cancelled = false;
    (async () => {
      const res = await agentService.getAgent(agentId);
      if (cancelled) return;
      if (!isApiError(res)) setAgentDefaultDescriptor(res.llm_model || "");
    })().catch((e) => {
      if (!cancelled) console.warn("[useAvailableModels] getAgent failed", e);
    });
    return () => {
      cancelled = true;
    };
  }, [agentId]);
  const mapDescriptor = useMemo4(
    () => (descriptor) => {
      if (!descriptor) return null;
      return models.find((m) => m.id === descriptor) || { id: descriptor, name: descriptor, provider: "unknown" };
    },
    [models]
  );
  return { models, agentDefaultDescriptor, loading, mapDescriptor };
}

// src/hooks/useContextUsage.ts
import { useEffect as useEffect3, useMemo as useMemo5, useRef as useRef3 } from "react";
import { contextAnalysisService } from "@dopomogai/api-client";
function signatureOf(opts) {
  const { cid, model, draftText, uploadedFileIds, msgCount, lastMsgId } = opts;
  return [cid, model, msgCount, lastMsgId || "", draftText.trim(), uploadedFileIds.join(",")].join("|");
}
function useContextUsage(effectiveModelDescriptor) {
  const client = useChatClient();
  const st = client.store.getState();
  const activeConversationId = st.activeConversationId || null;
  const abortRef = useRef3(null);
  const session = activeConversationId ? st.sessions[activeConversationId] : null;
  const uploadedFileIds = useMemo5(() => {
    if (!session) return [];
    return (session.draftAttachments || []).filter((a) => a.status === "uploaded" && !!a.fileId).map((a) => a.fileId);
  }, [session]);
  useEffect3(() => {
    const cid = activeConversationId;
    if (!cid) return;
    const sess = client.store.getState().sessions[cid];
    if (!sess) return;
    const hasPending = (sess.draftAttachments || []).some((a) => a.status === "queued" || a.status === "uploading");
    if (hasPending) return;
    const msgCount = sess.messages?.length || 0;
    const lastMsgId = msgCount ? sess.messages[msgCount - 1]?.id : void 0;
    const sig = signatureOf({
      cid,
      model: effectiveModelDescriptor || "",
      draftText: sess.draftText || "",
      uploadedFileIds,
      msgCount,
      lastMsgId
    });
    const prev = sess.contextUsage;
    const now2 = Date.now();
    if (prev && prev.signature === sig && now2 - prev.updatedAt < 15e3) return;
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    const run = async () => {
      try {
        const parts = [];
        if (sess.draftText?.trim()) parts.push({ type: "text", content: sess.draftText });
        for (const fid of uploadedFileIds) parts.push({ type: "file", file_id: fid, media_type: "application/octet-stream" });
        if (parts.length === 0) return;
        const resp = await contextAnalysisService.getUsageFromParts(
          {
            agent_id: client.store.getState().config.agentId,
            conversation_id: cid,
            parts,
            model_descriptor_override: effectiveModelDescriptor || void 0
          },
          { signal: controller.signal }
        );
        if (resp?.isError) return;
        const usage = {
          ...resp,
          updatedAt: now2,
          model_descriptor: effectiveModelDescriptor || "",
          signature: sig
        };
        client.store.getState().setContextUsage(cid, usage);
      } catch (e) {
        if (e?.name === "AbortError" || e?.name === "CanceledError") {
        } else {
          console.warn("[useContextUsage] failed", e);
        }
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
      }
    };
    const t = setTimeout(run, 700);
    return () => clearTimeout(t);
  }, [
    activeConversationId,
    client,
    effectiveModelDescriptor,
    uploadedFileIds.join(","),
    session?.messages?.length,
    session?.draftText
  ]);
  const headerContextUsage = useMemo5(() => {
    if (!activeConversationId) return null;
    const u = client.store.getState().sessions[activeConversationId]?.contextUsage;
    if (!u) return null;
    return {
      total_tokens: u.total_tokens,
      model_context_limit: u.model_context_limit,
      is_limit_exceeded: u.is_limit_exceeded,
      file_tokens_est: u.file_tokens_est
    };
  }, [client, activeConversationId, session?.contextUsage?.updatedAt]);
  return { headerContextUsage };
}

// src/hooks/useCostDetails.ts
import { useCallback, useState as useState2 } from "react";
import { conversationUsageService, isApiError as isApiError2 } from "@dopomogai/api-client";
function useCostDetails() {
  const client = useChatClient();
  const [isLoading, setLoading] = useState2(false);
  const [error, setError] = useState2(null);
  const [data, setData] = useState2(null);
  const loadForConversation = useCallback(async (conversationId) => {
    const cid = conversationId || client.store.getState().activeConversationId;
    if (!cid) return null;
    setLoading(true);
    setError(null);
    try {
      const res = await conversationUsageService.getConversationUsageLogs(cid);
      if (isApiError2(res)) {
        setError(res.message || "Failed to load usage logs");
        setData(null);
        return null;
      }
      setData(res);
      return res;
    } catch (e) {
      setError(e?.message || "Failed to load usage logs");
      setData(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [client]);
  return { isLoading, error, data, loadForConversation };
}

// src/hooks/useHeroSuggestions.ts
import { useEffect as useEffect4, useRef as useRef4, useState as useState3 } from "react";
import { suggestionsService, widgetsService as widgetsService2 } from "@dopomogai/api-client";

// src/hooks/internal/compatApiGuard.ts
function requireCompatApiMethod(method, compatFallbackEnabled, strictErrorMessage) {
  if (typeof method === "function") return true;
  if (compatFallbackEnabled) return false;
  throw new Error(strictErrorMessage);
}

// src/hooks/useHeroSuggestions.ts
function useHeroSuggestions(agentId, isEmptyConversation, limit = 4) {
  const client = useChatClient();
  const widgetVersionId = client.config.widgetVersionId;
  const widgetSuggestionsApi = widgetsService2;
  const compatFallbackEnabled = client.config.compatApiFallbackEnabled !== false;
  const [items, setItems] = useState3([]);
  const [loading, setLoading] = useState3(false);
  const abortRef = useRef4(null);
  useEffect4(() => {
    if (!isEmptyConversation) return;
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    (async () => {
      try {
        if (widgetVersionId) {
          const getWidgetSuggestions = widgetSuggestionsApi.getWidgetSuggestions;
          const methodAvailable = requireCompatApiMethod(
            getWidgetSuggestions,
            compatFallbackEnabled,
            "widgetsService.getWidgetSuggestions is unavailable in strict mode."
          );
          if (!methodAvailable) {
            setItems([]);
            return;
          }
          const res2 = await getWidgetSuggestions(widgetVersionId, {
            limit,
            force: false,
            persist: false
          });
          if (res2?.isError) {
            setItems([]);
            return;
          }
          setItems(res2.suggestions || []);
          return;
        }
        if (!agentId) return;
        const res = await suggestionsService.getAgentSuggestions(agentId, {
          limit,
          scope: "new_conversation",
          force: false
        });
        if (res?.isError) {
          setItems([]);
          return;
        }
        setItems(res.suggestions || []);
      } catch (e) {
        if (e?.name !== "AbortError") console.warn("[useHeroSuggestions] failed", e?.message || e);
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [agentId, isEmptyConversation, limit, widgetVersionId, compatFallbackEnabled]);
  return { items, loading };
}

// src/hooks/useNextPromptSuggestions.ts
import { useEffect as useEffect5, useMemo as useMemo6, useRef as useRef5, useState as useState4 } from "react";
import { suggestionsService as suggestionsService2, widgetsService as widgetsService3 } from "@dopomogai/api-client";
function findLastMessageIdByRole(messages, role) {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i]?.role === role) return messages[i].id || null;
  }
  return null;
}
function useNextPromptSuggestions(limit = 4) {
  const { messages, activeId, status } = useChat();
  const client = useChatClient();
  const widgetVersionId = client.config.widgetVersionId;
  const widgetSuggestionsApi = widgetsService3;
  const compatFallbackEnabled = client.config.compatApiFallbackEnabled !== false;
  const [items, setItems] = useState4([]);
  const [loading, setLoading] = useState4(false);
  const abortRef = useRef5(null);
  const lastAssistantId = useMemo6(() => findLastMessageIdByRole(messages, "assistant"), [messages]);
  const lastUserId = useMemo6(() => findLastMessageIdByRole(messages, "user"), [messages]);
  const ready = useMemo6(() => {
    if (!activeId) return false;
    if (status !== "IDLE") return false;
    return !!lastAssistantId;
  }, [activeId, status, lastAssistantId]);
  useEffect5(() => {
    if (!ready || !activeId) return;
    const sess = client.store.getState().sessions[activeId];
    const cached = sess?.nextSuggestions;
    if (cached && cached.forAssistantMessageId === lastAssistantId && cached.forUserMessageId === lastUserId && Array.isArray(cached.items) && cached.items.length > 0) {
      setItems(cached.items);
      return;
    }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    (async () => {
      try {
        if (widgetVersionId) {
          const getWidgetConversationSuggestions = widgetSuggestionsApi.getWidgetConversationSuggestions;
          const methodAvailable = requireCompatApiMethod(
            getWidgetConversationSuggestions,
            compatFallbackEnabled,
            "widgetsService.getWidgetConversationSuggestions is unavailable in strict mode."
          );
          if (!methodAvailable) {
            setItems([]);
            return;
          }
          const res2 = await getWidgetConversationSuggestions(widgetVersionId, activeId, {
            limit,
            force: false,
            persist: false
          });
          const list2 = res2?.suggestions || [];
          setItems(list2);
          client.store.getState().setNextSuggestions?.(activeId, {
            items: list2,
            forAssistantMessageId: lastAssistantId,
            forUserMessageId: lastUserId,
            createdAt: Date.now()
          });
          return;
        }
        const res = await suggestionsService2.getConversationSuggestions(activeId, {
          limit,
          scope: "next_message",
          force: false
        });
        const list = res?.suggestions || [];
        setItems(list);
        client.store.getState().setNextSuggestions?.(activeId, {
          items: list,
          forAssistantMessageId: lastAssistantId,
          forUserMessageId: lastUserId,
          createdAt: Date.now()
        });
      } catch (e) {
        if (e?.name !== "AbortError") {
          console.warn("[useNextPromptSuggestions] failed", e?.message || e);
        }
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [ready, activeId, limit, client, lastAssistantId, lastUserId, widgetVersionId, compatFallbackEnabled]);
  return { items, loading, ready };
}

// src/hooks/useSessionActivation.ts
import { useCallback as useCallback2 } from "react";
function useSessionActivation(opts) {
  const client = useChatClient();
  const activate = useCallback2(
    (id, options) => {
      const load = options?.load ?? true;
      const st = client.store.getState();
      st.initSession(id);
      st.setActiveConversationId(id);
      if (load) void client.conversationManager.loadConversation(id);
      opts?.onActivate?.(id);
    },
    [client, opts]
  );
  const createAndActivate = useCallback2(
    (options) => {
      const id = crypto.randomUUID();
      const title = options?.title || "New Chat";
      const st = client.store.getState();
      st.initSession(id);
      const tabs = st.openTabs || [];
      st.setOpenTabs([...tabs, { id, title }]);
      st.setActiveConversationId(id);
      opts?.onActivate?.(id);
      return id;
    },
    [client, opts]
  );
  return { activate, createAndActivate };
}

// src/hooks/useThreads.ts
import { useEffect as useEffect6, useState as useState5 } from "react";
import { threadsService } from "@dopomogai/api-client";
function useThreads(conversationId, enabled = true) {
  const client = useChatClient();
  const [loading, setLoading] = useState5(false);
  const [rows, setRows] = useState5([]);
  const [error, setError] = useState5(null);
  const cid = conversationId ?? client.store.getState().activeConversationId ?? null;
  const refresh = async () => {
    if (!enabled || !cid) return;
    setLoading(true);
    setError(null);
    try {
      const res = await threadsService.listThreads(cid);
      if (res?.isError) {
        setError(res.message || "Failed to list threads");
        setRows([]);
      } else {
        setRows(res);
      }
    } catch (e) {
      setError(e?.message || "Failed to list threads");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect6(() => {
    if (!enabled) return;
    void refresh();
  }, [cid, enabled]);
  const create = async (messageIndex, newAgentId) => {
    if (!enabled || !cid) return null;
    setLoading(true);
    try {
      const res = await threadsService.createThread(cid, { message_index: messageIndex, new_agent_id: newAgentId });
      if (res?.isError) {
        setError(res.message || "Failed to create thread");
        return null;
      }
      await refresh();
      return res;
    } finally {
      setLoading(false);
    }
  };
  const open = async (childConversationId) => {
    await client.getConversation(childConversationId);
  };
  return { conversationId: cid, loading, error, rows, refresh, create, open };
}

// src/hooks/useKBIntegration.ts
function useKBIntegration() {
  const client = useChatClient();
  const attachFileIdsToDraft = (ids) => {
    const cid = client.store.getState().activeConversationId;
    if (!cid) return;
    ids.forEach((fid) => {
      client.store.getState().addAttachment(cid, {
        clientId: crypto.randomUUID(),
        fileId: fid,
        url: client ? `${client.config.apiBaseUrl}/api/v1/files/${fid}/download` : "",
        name: `file-${fid.substring(0, 6)}`,
        type: "file",
        status: "uploaded"
      });
    });
  };
  const attachGroupToMessage = async (messageId, groupId) => {
    const cid = client.store.getState().activeConversationId || "";
    if (!cid) return false;
    return client.attachGroupToMessage(cid, messageId, groupId);
  };
  return { attachFileIdsToDraft, attachGroupToMessage };
}

// src/hooks/useDeltaSync.ts
import { useEffect as useEffect7 } from "react";
function useDeltaSync() {
  useEffect7(() => {
  }, []);
}

// src/hooks/useComments.ts
import { useCallback as useCallback3, useEffect as useEffect8, useState as useState6 } from "react";
import { commentsService } from "@dopomogai/api-client";
function useComments(conversationId, messageIndex, enabled = true) {
  const client = useChatClient();
  const cid = conversationId ?? client.store.getState().activeConversationId ?? null;
  const [items, setItems] = useState6([]);
  const [loading, setLoading] = useState6(false);
  const [error, setError] = useState6(null);
  const refresh = useCallback3(async () => {
    if (!enabled || !cid || messageIndex == null) return;
    setLoading(true);
    setError(null);
    try {
      const res = await commentsService.listComments(cid, messageIndex);
      if (res?.isError) {
        setError(res.message || "Failed to list comments");
        setItems([]);
      } else {
        setItems(res);
      }
    } catch (e) {
      setError(e?.message || "Failed to list comments");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [cid, messageIndex, enabled]);
  useEffect8(() => {
    void refresh();
  }, [refresh]);
  const add = async (body) => {
    if (!enabled || !cid || messageIndex == null) return null;
    const res = await commentsService.addComment(cid, messageIndex, { body });
    if (res?.isError) return null;
    await refresh();
    return res;
  };
  const update = async (commentId, patch) => {
    if (!enabled || !cid) return null;
    const res = await commentsService.updateComment(cid, commentId, patch);
    if (res?.isError) return null;
    await refresh();
    return res;
  };
  return { items, loading, error, refresh, add, update };
}

// src/hooks/useFiles.ts
import { useState as useState7, useCallback as useCallback4, useMemo as useMemo7 } from "react";
import * as apiClient from "@dopomogai/api-client";
import { FilePurposeEnum } from "@dopomogai/types";
var { filesService, isApiError: isApiError3 } = apiClient;
var widgetFilesService2 = apiClient.widgetFilesService;
function useFiles(options) {
  const client = useChatClient();
  const agentId = client.config.agentId;
  const widgetVersionId = client.config.widgetVersionId;
  const compatFallbackEnabled = client.config.compatApiFallbackEnabled !== false;
  const isVisitorWidget = !!widgetVersionId;
  const [activeUploadsCount, setActiveUploadsCount] = useState7(0);
  const isUploading = useMemo7(() => activeUploadsCount > 0, [activeUploadsCount]);
  const [isLoadingList, setIsLoadingList] = useState7(false);
  const [error, setError] = useState7(null);
  const [files, setFiles] = useState7([]);
  const uploadFile = useCallback4(
    async (file, purpose = FilePurposeEnum.AgentInput) => {
      setActiveUploadsCount((prev) => prev + 1);
      setError(null);
      try {
        if (!agentId) {
          throw new Error("Cannot upload file: Agent ID is missing in client configuration.");
        }
        let response;
        if (isVisitorWidget) {
          const upload = widgetFilesService2?.upload;
          const uploadMethodAvailable = requireCompatApiMethod(
            upload,
            compatFallbackEnabled,
            "Widget file upload API is unavailable in the current API client build."
          );
          if (!uploadMethodAvailable) {
            const missingMessage = "Widget file upload API is unavailable in the current API client build.";
            setError(missingMessage);
            return null;
          }
          response = await upload(widgetVersionId, file, {
            purpose,
            agentId
          });
        } else {
          response = await filesService.upload(file, purpose, agentId);
        }
        if (isApiError3(response)) {
          throw new Error(response.message || "Upload failed");
        }
        const uploadedFile = response;
        setFiles((prev) => [uploadedFile, ...prev]);
        options?.onUploadSuccess?.(uploadedFile);
        return uploadedFile;
      } catch (err) {
        const msg = err.message || "An unexpected error occurred during upload";
        console.error("Upload error:", err);
        setError(msg);
        options?.onUploadError?.(err);
        return null;
      } finally {
        setActiveUploadsCount((prev) => Math.max(0, prev - 1));
      }
    },
    [options, agentId, isVisitorWidget, widgetVersionId, compatFallbackEnabled]
  );
  const uploadMultipleFiles = useCallback4(
    async (fileList, purpose = FilePurposeEnum.AgentInput) => {
      const results = [];
      for (const file of fileList) {
        const result = await uploadFile(file, purpose);
        if (result) results.push(result);
      }
      return results;
    },
    [uploadFile]
  );
  const listFiles = useCallback4(
    async (_purpose = FilePurposeEnum.AgentInput, _limit = 20, _offset = 0) => {
      if (isVisitorWidget) {
        setFiles([]);
        return [];
      }
      setIsLoadingList(true);
      setError(null);
      try {
        const response = await filesService.list(_purpose, _limit, _offset);
        if (isApiError3(response)) throw new Error(response.message || "Failed to list files");
        const fileList = response;
        setFiles(fileList);
        return fileList;
      } catch (err) {
        setError(err.message || "Failed to fetch file list");
        return [];
      } finally {
        setIsLoadingList(false);
      }
    },
    [isVisitorWidget]
  );
  const deleteFile = useCallback4(
    async (fileId) => {
      if (isVisitorWidget) return false;
      setError(null);
      try {
        const response = await filesService.remove(fileId);
        if (isApiError3(response)) throw new Error(response.message || "Failed to delete file");
        setFiles((prev) => prev.filter((f) => f.id !== fileId));
        options?.onDeleteSuccess?.(fileId);
        return true;
      } catch (err) {
        setError(err.message || "Failed to delete file");
        return false;
      }
    },
    [options, isVisitorWidget]
  );
  const getDownloadUrl = useCallback4(
    (fileId) => {
      if (isVisitorWidget) {
        const getDownloadUrl2 = widgetFilesService2?.getDownloadUrl;
        const methodAvailable = requireCompatApiMethod(
          getDownloadUrl2,
          compatFallbackEnabled,
          "Widget file download URL API is unavailable in strict mode."
        );
        if (!methodAvailable) return "";
        const downloadUrl = getDownloadUrl2(widgetVersionId, fileId);
        if (downloadUrl) return downloadUrl;
        return "";
      }
      return filesService.getDownloadUrl(fileId);
    },
    [isVisitorWidget, widgetVersionId, compatFallbackEnabled]
  );
  return {
    uploadFile,
    uploadMultipleFiles,
    listFiles,
    deleteFile,
    files,
    isUploading,
    activeUploadsCount,
    isLoadingList,
    error,
    getDownloadUrl
  };
}

// src/hooks/useMessageUI.ts
import { useMemo as useMemo8, useCallback as useCallback5 } from "react";
import { useStoreWithEqualityFn } from "zustand/traditional";
var EMPTY_UI = Object.freeze({ renderers: Object.freeze({}) });
var useMessageUI = (messageId) => {
  const client = useChatClient();
  const selector = useMemo8(
    () => (s) => {
      const activeId2 = s.activeConversationId;
      const session = activeId2 ? s.sessions?.[activeId2] : void 0;
      const ui2 = session?.messageUI?.[messageId] ?? EMPTY_UI;
      return { activeId: activeId2, ui: ui2 };
    },
    [messageId]
  );
  const equality = useCallback5(
    (a, b) => a.activeId === b.activeId && a.ui === b.ui,
    []
  );
  const { activeId, ui } = useStoreWithEqualityFn(client.store, selector, equality);
  const setRendererState = (rendererKey, partial) => {
    if (!activeId) return;
    if (!client.store.getState().sessions?.[activeId]) return;
    client.store.getState().setRendererUI(activeId, messageId, rendererKey, partial);
  };
  const toggleFlag = (rendererKey, flagKey) => {
    if (!activeId) return;
    if (!client.store.getState().sessions?.[activeId]) return;
    client.store.getState().toggleRendererUI(activeId, messageId, rendererKey, flagKey);
  };
  const getRendererState = (rendererKey, defaultValue) => {
    const val = ui.renderers[rendererKey];
    return val ?? defaultValue;
  };
  return { ui, getRendererState, setRendererState, toggleFlag };
};

// src/hooks/useFileMetadata.ts
import { useEffect as useEffect9, useMemo as useMemo9, useRef as useRef6, useState as useState8 } from "react";
import { filesService as filesService2 } from "@dopomogai/api-client";
var DEFAULT_TTL_MS = 3 * 60 * 60 * 1e3;
var memoryCache = /* @__PURE__ */ new Map();
var LS_KEY = "dopomogai:file-meta-cache:v1";
function now() {
  return Date.now();
}
function readLocalCache() {
  if (typeof window === "undefined") return /* @__PURE__ */ new Map();
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return /* @__PURE__ */ new Map();
    const obj = JSON.parse(raw);
    const map = /* @__PURE__ */ new Map();
    for (const [k, v] of Object.entries(obj)) {
      if (v && v.data && v.ts) map.set(k, v);
    }
    return map;
  } catch {
    return /* @__PURE__ */ new Map();
  }
}
function writeLocalCache(cache) {
  if (typeof window === "undefined") return;
  try {
    const obj = {};
    for (const [k, v] of cache.entries()) obj[k] = v;
    localStorage.setItem(LS_KEY, JSON.stringify(obj));
  } catch {
  }
}
(() => {
  const ls = readLocalCache();
  ls.forEach((v, k) => memoryCache.set(k, v));
})();
function isStale(entry, ttlMs) {
  return now() - entry.ts > ttlMs;
}
function getCachedFileMetadata(fileId) {
  if (!fileId) return void 0;
  const e = memoryCache.get(fileId);
  return e?.data;
}
async function fetchAndCacheSingle(fileId) {
  const res = await filesService2.getMetadata(fileId);
  if (res?.isError) return null;
  const data = res;
  const entry = { data, ts: now() };
  memoryCache.set(fileId, entry);
  writeLocalCache(memoryCache);
  return data;
}
async function prefetchFileMetadata(fileIds, opts) {
  const unique = Array.from(new Set(fileIds.filter(Boolean)));
  if (unique.length === 0) return;
  const miss = [];
  for (const id of unique) {
    const entry = memoryCache.get(id);
    if (!entry) {
      miss.push(id);
    }
  }
  if (miss.length === 0) return;
  const chunkSize = Math.max(1, Math.min(opts?.chunkSize ?? 200, 500));
  for (let i = 0; i < miss.length; i += chunkSize) {
    const slice = miss.slice(i, i + chunkSize);
    try {
      const res = await filesService2.getMetadataBatch(slice);
      if (res?.isError) {
        await Promise.all(slice.map((id) => fetchAndCacheSingle(id)));
        continue;
      }
      const items = res.items || [];
      for (const it of items) {
        memoryCache.set(it.id, { data: it, ts: now() });
      }
      writeLocalCache(memoryCache);
      const returned = new Set(items.map((it) => it.id));
      const missingStill = slice.filter((id) => !returned.has(id));
      if (missingStill.length) {
        await Promise.all(missingStill.map((id) => fetchAndCacheSingle(id)));
      }
    } catch {
      await Promise.all(slice.map((id) => fetchAndCacheSingle(id)));
    }
  }
}
function useFileMetadata(fileId, opts) {
  const ttlMs = opts?.ttlMs ?? DEFAULT_TTL_MS;
  const lazy = !!opts?.lazy;
  const [metadata, setMetadata] = useState8(() => getCachedFileMetadata(fileId));
  const [error, setError] = useState8(null);
  const [loading, setLoading] = useState8(false);
  const mountedRef = useRef6(true);
  const needsFetch = useMemo9(() => {
    if (!fileId) return false;
    const e = memoryCache.get(fileId);
    if (!e) return true;
    return isStale(e, ttlMs);
  }, [fileId, ttlMs]);
  const doFetch = async () => {
    if (!fileId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await fetchAndCacheSingle(fileId);
      if (!mountedRef.current) return;
      if (!res) {
        setError("Failed to fetch file metadata");
        return;
      }
      setMetadata(res);
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e?.message || "Failed to fetch file metadata");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };
  useEffect9(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  useEffect9(() => {
    setMetadata(getCachedFileMetadata(fileId));
    setError(null);
    if (fileId && !lazy && needsFetch) {
      void doFetch();
    }
  }, [fileId, lazy, needsFetch]);
  return {
    metadata,
    isLoading: loading,
    error,
    refresh: doFetch
  };
}
var fileMetadataCache = {
  get: getCachedFileMetadata,
  set: (fileId, data) => {
    memoryCache.set(fileId, { data, ts: now() });
    writeLocalCache(memoryCache);
  },
  has: (fileId) => memoryCache.has(fileId),
  prefetch: prefetchFileMetadata
};

// src/compat/frontendActions.ts
var DEFAULT_FRONTEND_ACTION_ALLOWLIST = [
  "navigate",
  "open_section",
  "select_preset",
  "toggle_feature",
  "set_copy",
  "generate_quote",
  "submit_lead_email",
  "fill_field"
];
var actionNameAliases = {
  navigate: "navigate",
  go_to: "navigate",
  goto: "navigate",
  open_page: "navigate",
  open_section: "open_section",
  opensection: "open_section",
  open: "open_section",
  scroll_to: "open_section",
  scrollto: "open_section",
  select_preset: "select_preset",
  selectpreset: "select_preset",
  preset: "select_preset",
  toggle_feature: "toggle_feature",
  togglefeature: "toggle_feature",
  set_copy: "set_copy",
  setcopy: "set_copy",
  generate_quote: "generate_quote",
  generatequote: "generate_quote",
  submit_lead_email: "submit_lead_email",
  submitleademail: "submit_lead_email",
  fill_field: "fill_field",
  fillfield: "fill_field"
};
function aliasName(name) {
  return actionNameAliases[(name || "").toLowerCase()] ?? null;
}
function normalizeSyncResult(result) {
  return result === void 0 ? { status: "success" } : result;
}
function getExecutionContext(candidate) {
  if (!candidate || typeof candidate !== "object") return void 0;
  const toolCallId = candidate.toolCallId;
  const conversationId = candidate.conversationId;
  if (typeof toolCallId !== "string" || typeof conversationId !== "string") return void 0;
  return candidate;
}
function normalizeFrontendActionsArgs(raw) {
  const normalized = [];
  if (!raw || typeof raw !== "object") return { actions: normalized };
  const pushAction = (name, params) => {
    const aliased = aliasName(name);
    if (!aliased) return;
    if (aliased === "navigate") {
      const path = typeof params === "string" ? params : params?.path;
      if (typeof path === "string" && path.trim()) normalized.push({ name: "navigate", params: { path } });
      return;
    }
    if (aliased === "open_section") {
      let id = typeof params === "string" ? params : params?.id;
      if (typeof id === "string" && id.startsWith("#")) id = id.slice(1);
      if (typeof id === "string" && id.trim()) normalized.push({ name: "open_section", params: { id } });
      return;
    }
    if (aliased === "select_preset") {
      const id = typeof params === "string" ? params : params?.id;
      if (typeof id === "string" && id.trim()) normalized.push({ name: "select_preset", params: { id } });
      return;
    }
    if (aliased === "toggle_feature") {
      const id = params?.id;
      const enabled = typeof params?.enabled === "boolean" ? params.enabled : Boolean(params?.on ?? params?.value);
      if (typeof id === "string" && id.trim()) normalized.push({ name: "toggle_feature", params: { id, enabled } });
      return;
    }
    if (aliased === "set_copy") {
      const { field, value, index } = params || {};
      if (typeof field === "string" && typeof value === "string" && ["heroTitle", "heroSubtitle", "bullets", "integrations"].includes(field)) {
        normalized.push({ name: "set_copy", params: { field, value, index } });
      }
      return;
    }
    if (aliased === "generate_quote") {
      normalized.push({ name: "generate_quote", params: {} });
      return;
    }
    if (aliased === "submit_lead_email") {
      const email = typeof params === "string" ? params : params?.email;
      if (typeof email === "string" && email.trim()) normalized.push({ name: "submit_lead_email", params: { email } });
      return;
    }
    if (aliased === "fill_field") {
      const { formId, fieldId, value } = params || {};
      if (typeof formId === "string" && typeof fieldId === "string") {
        normalized.push({
          name: "fill_field",
          params: { formId, fieldId, value: String(value ?? "") }
        });
      }
    }
  };
  if (Array.isArray(raw.actions)) {
    for (const action of raw.actions) {
      if (!action || typeof action !== "object") continue;
      const name = action.name || action.action || action.type;
      pushAction(name, action.params ?? action.args ?? action.payload ?? action);
    }
    return { actions: normalized };
  }
  if (Array.isArray(raw.steps)) {
    for (const step of raw.steps) {
      if (!step || typeof step !== "object") continue;
      const name = step.name || step.action || step.type;
      pushAction(name, step.params ?? step.args ?? step.payload ?? step);
    }
    return { actions: normalized };
  }
  if (typeof raw.go_to === "string") pushAction("navigate", { path: raw.go_to });
  if (typeof raw.open === "string") pushAction("open_section", { id: raw.open });
  if (typeof raw.navigate === "string") pushAction("navigate", { path: raw.navigate });
  if (raw.action || raw.type) pushAction(raw.action ?? raw.type, raw.params ?? raw.args ?? raw.payload ?? {});
  for (const [key, value] of Object.entries(raw)) {
    if (["actions", "steps", "plan", "args", "params", "payload", "tool_name", "name", "action", "type"].includes(key)) {
      continue;
    }
    const aliased = aliasName(key);
    if (aliased) pushAction(aliased, value);
  }
  return { actions: normalized };
}
async function runFrontendActions(args, context, allowed, toolExecutionContext) {
  const normalized = normalizeFrontendActionsArgs(args);
  if (!normalized.actions.length) {
    return {
      status: "error",
      executed: [],
      count: 0,
      results: [],
      message: "No recognizable actions provided."
    };
  }
  if (!context) {
    return {
      status: "error",
      executed: [],
      count: 0,
      results: [],
      message: "No frontend action context provided."
    };
  }
  const executionContext = toolExecutionContext ?? getExecutionContext(args) ?? void 0;
  const executed = [];
  const results = [];
  try {
    for (const action of normalized.actions) {
      if (allowed && !allowed.includes(action.name)) {
        results.push({ name: action.name, result: { status: "error", code: "not_allowed" } });
        continue;
      }
      switch (action.name) {
        case "select_preset": {
          const result = await Promise.resolve(context.selectPreset(action.params.id, executionContext));
          results.push({ name: action.name, result: normalizeSyncResult(result) });
          break;
        }
        case "toggle_feature": {
          const result = await Promise.resolve(
            context.setFeature(action.params.id, action.params.enabled, executionContext)
          );
          results.push({ name: action.name, result: normalizeSyncResult(result) });
          break;
        }
        case "open_section": {
          const result = await Promise.resolve(context.openSection(action.params.id, executionContext));
          results.push({ name: action.name, result: normalizeSyncResult(result) });
          break;
        }
        case "navigate": {
          const result = await Promise.resolve(context.safeNavigate(action.params.path, executionContext));
          results.push({ name: action.name, result: normalizeSyncResult(result) });
          break;
        }
        case "set_copy": {
          const result = await Promise.resolve(
            context.setPersonalizedCopy(action.params.field, action.params.value, action.params.index, executionContext)
          );
          results.push({ name: action.name, result: normalizeSyncResult(result) });
          break;
        }
        case "generate_quote": {
          const result = await context.openQuoteWithCurrentConfig(executionContext);
          results.push({ name: action.name, result });
          break;
        }
        case "submit_lead_email": {
          const result = await context.submitLeadEmail(action.params.email, executionContext);
          results.push({ name: action.name, result });
          break;
        }
        case "fill_field": {
          const result = await Promise.resolve(
            context.fillFormField(action.params.formId, action.params.fieldId, action.params.value, executionContext)
          );
          results.push({ name: action.name, result: normalizeSyncResult(result) });
          break;
        }
      }
      executed.push(action.name);
    }
    return { status: "success", executed, count: executed.length, results };
  } catch (error) {
    return {
      status: "error",
      executed,
      count: executed.length,
      results,
      message: error?.message || "Unexpected error."
    };
  }
}

// src/compat/createFrontendActions.ts
function createFrontendActions(context, extra, allowed) {
  const result = {
    async frontend_actions(args, toolExecutionContext) {
      return runFrontendActions(args, context, allowed, toolExecutionContext);
    }
  };
  if (extra) {
    for (const [key, value] of Object.entries(extra)) {
      if (typeof value === "function") {
        result[key] = value;
      }
    }
  }
  return result;
}

// src/compat/defineHooks.ts
function briefForHook(hook) {
  return {
    name: hook.name,
    what: hook.description,
    example: hook.examples && hook.examples.length > 0 ? hook.examples[0] : null,
    safety: hook.safety?.confirm ? "requires_confirm" : void 0
  };
}
function buildPerTurnPrompt(manifest) {
  const lines = [];
  lines.push('You can take actions by calling the tool "frontend_actions".');
  lines.push("Only use the following hooks (strict allowlist):");
  lines.push(`- ${manifest.allow.join(", ") || "(none)"}`);
  lines.push("Batch multiple steps in a single call when appropriate: { actions: [ { name, params }, ... ] }.");
  lines.push("If a hook is potentially destructive and marked as requiring confirmation, ask the user first or use confirm_action.");
  if (manifest.prompt && manifest.prompt.trim()) {
    lines.push("");
    lines.push(manifest.prompt.trim());
  }
  return lines.join("\n");
}
function isFrontendToolContext(value) {
  if (!value || typeof value !== "object") return false;
  const candidate = value;
  return typeof candidate.selectPreset === "function" && typeof candidate.setFeature === "function" && typeof candidate.openSection === "function" && typeof candidate.safeNavigate === "function" && typeof candidate.setPersonalizedCopy === "function" && typeof candidate.openQuoteWithCurrentConfig === "function" && typeof candidate.submitLeadEmail === "function" && typeof candidate.fillFormField === "function";
}
function resolveFrontendContext(primary, fallback) {
  if (isFrontendToolContext(primary)) return primary;
  if (isFrontendToolContext(fallback)) return fallback;
  return void 0;
}
function defineHooks(manifest, handlers, options) {
  const allowedCustom = new Set(manifest.allow || []);
  const builtInsAllow = options?.builtInsAllowlist || DEFAULT_FRONTEND_ACTION_ALLOWLIST;
  const hooksBrief = (manifest.hooks || []).map(briefForHook);
  const perTurnPrompt = buildPerTurnPrompt(manifest);
  const perTurnContext = () => {
    const pageId = options?.pageId || manifest.pageId || "unknown";
    const base = {
      page: {
        id: pageId,
        url: typeof window !== "undefined" ? window.location.href : "",
        title: typeof document !== "undefined" ? document.title : ""
      },
      hooks_manifest_version: manifest.version,
      hooks_manifest_brief: hooksBrief,
      timestamp_utc: (/* @__PURE__ */ new Date()).toISOString()
    };
    const extra = typeof options?.extraContext === "function" ? options.extraContext() : void 0;
    return extra ? { ...base, ...extra } : base;
  };
  const frontendActions = async (rawArgs, runtimeContext) => {
    const args = rawArgs && typeof rawArgs === "object" ? rawArgs : {};
    const actions = Array.isArray(args.actions) ? args.actions : [];
    const customCalls = actions.filter((action) => {
      const name = action?.name || action?.action || action?.type;
      return typeof name === "string" && allowedCustom.has(name);
    });
    const builtinCalls = actions.filter((action) => {
      const name = action?.name || action?.action || action?.type;
      return typeof name === "string" && !allowedCustom.has(name);
    });
    const results = [];
    let executedCount = 0;
    const frontendContext = resolveFrontendContext(runtimeContext, options?.headlessContext);
    const handlerContext = frontendContext ?? runtimeContext;
    for (const step of customCalls) {
      const name = step.name || step.action || step.type;
      if (!name) continue;
      try {
        const handler = handlers[name];
        if (typeof handler !== "function") {
          results.push({ name, result: { status: "error", code: "no_handler" } });
          continue;
        }
        const result = await handler(step.params ?? step.args ?? step.payload, handlerContext);
        results.push({ name, result: result ?? { status: "success" } });
        executedCount += 1;
      } catch (error) {
        results.push({ name, result: { status: "error", message: error?.message || "Hook failed" } });
      }
    }
    if (builtinCalls.length > 0) {
      const builtInResult = await runFrontendActions({ actions: builtinCalls }, frontendContext, builtInsAllow, runtimeContext);
      results.push(...builtInResult.results || []);
      executedCount += builtInResult.count || 0;
    }
    return {
      status: "success",
      executed: results.map((result) => result.name),
      count: executedCount,
      results
    };
  };
  return {
    overrides: {
      perTurnContext,
      perTurnPrompt,
      headlessContext: options?.headlessContext,
      toolRegistry: options?.toolRegistry ?? void 0
    },
    headlessTools: {
      frontend_actions: frontendActions
    }
  };
}

// src/compat/toolRegistry.tsx
import { useState as useState9 } from "react";
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var containerStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  padding: 16,
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  backgroundColor: "#fff"
};
var buttonRowStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 12
};
var secondaryButtonStyle = {
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  padding: "10px 14px",
  backgroundColor: "#fff",
  cursor: "pointer"
};
var primaryButtonStyle = {
  border: "1px solid #0f172a",
  borderRadius: 8,
  padding: "10px 14px",
  backgroundColor: "#0f172a",
  color: "#fff",
  cursor: "pointer"
};
var fieldStyle = {
  width: "100%",
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  padding: "10px 12px",
  fontSize: 14,
  boxSizing: "border-box"
};
var tagStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  padding: "4px 10px",
  fontSize: 12,
  backgroundColor: "#f1f5f9",
  color: "#334155"
};
var ConfirmActionTool = ({
  args,
  onSubmit,
  onCancel
}) => {
  return /* @__PURE__ */ jsxs2("div", { style: containerStyle, children: [
    /* @__PURE__ */ jsx3("p", { style: { margin: 0, color: "#334155" }, children: args.message || "Are you sure you want to proceed?" }),
    /* @__PURE__ */ jsxs2("div", { style: buttonRowStyle, children: [
      /* @__PURE__ */ jsx3("button", { type: "button", onClick: onCancel, style: secondaryButtonStyle, children: args.cancelLabel || "Cancel" }),
      /* @__PURE__ */ jsx3("button", { type: "button", onClick: () => onSubmit({ confirmed: true }), style: primaryButtonStyle, children: args.confirmLabel || "Yes, confirm" })
    ] })
  ] });
};
var RenderFormTool = ({
  args,
  onSubmit,
  onCancel
}) => {
  const [formData, setFormData] = useState9(
    () => (args.fields || []).reduce((acc, field) => ({ ...acc, [field.name]: "" }), {})
  );
  return /* @__PURE__ */ jsxs2(
    "form",
    {
      onSubmit: (event) => {
        event.preventDefault();
        onSubmit(formData);
      },
      style: containerStyle,
      children: [
        /* @__PURE__ */ jsx3("h3", { style: { margin: 0, fontSize: 16, color: "#0f172a" }, children: args.title || "Please provide your details" }),
        (args.fields || []).map((field) => /* @__PURE__ */ jsxs2("label", { style: { display: "flex", flexDirection: "column", gap: 6 }, children: [
          /* @__PURE__ */ jsx3("span", { style: { fontSize: 13, color: "#334155" }, children: field.label }),
          field.type === "textarea" ? /* @__PURE__ */ jsx3(
            "textarea",
            {
              name: field.name,
              required: field.required,
              placeholder: field.placeholder,
              value: formData[field.name] ?? "",
              onChange: (event) => setFormData((current) => ({ ...current, [field.name]: event.target.value })),
              rows: 3,
              style: fieldStyle
            }
          ) : /* @__PURE__ */ jsx3(
            "input",
            {
              name: field.name,
              type: field.type || "text",
              required: field.required,
              placeholder: field.placeholder,
              value: formData[field.name] ?? "",
              onChange: (event) => setFormData((current) => ({ ...current, [field.name]: event.target.value })),
              style: fieldStyle
            }
          )
        ] }, field.name)),
        /* @__PURE__ */ jsxs2("div", { style: buttonRowStyle, children: [
          /* @__PURE__ */ jsx3("button", { type: "button", onClick: onCancel, style: secondaryButtonStyle, children: "Cancel" }),
          /* @__PURE__ */ jsx3("button", { type: "submit", style: primaryButtonStyle, children: args.submitLabel || "Submit" })
        ] })
      ]
    }
  );
};
var RenderCardTool = ({
  args,
  onSubmit,
  onCancel
}) => {
  return /* @__PURE__ */ jsxs2("div", { style: { ...containerStyle, overflow: "hidden", padding: 0 }, children: [
    args.imageUrl ? /* @__PURE__ */ jsx3(
      "img",
      {
        src: args.imageUrl,
        alt: args.title || "Card image",
        style: { width: "100%", height: 160, objectFit: "cover" }
      }
    ) : null,
    /* @__PURE__ */ jsxs2("div", { style: { display: "flex", flexDirection: "column", gap: 12, padding: 16 }, children: [
      args.tags && args.tags.length > 0 ? /* @__PURE__ */ jsx3("div", { style: { display: "flex", flexWrap: "wrap", gap: 8 }, children: args.tags.map((tag, index) => /* @__PURE__ */ jsx3("span", { style: tagStyle, children: tag.text }, `${tag.text}-${index}`)) }) : null,
      /* @__PURE__ */ jsxs2("div", { style: { display: "flex", flexDirection: "column", gap: 6 }, children: [
        /* @__PURE__ */ jsx3("h3", { style: { margin: 0, fontSize: 18, color: "#0f172a" }, children: args.title }),
        /* @__PURE__ */ jsx3("p", { style: { margin: 0, color: "#334155", fontSize: 14 }, children: args.description })
      ] }),
      args.actions && args.actions.length > 0 ? /* @__PURE__ */ jsx3("div", { style: buttonRowStyle, children: args.actions.map((action) => /* @__PURE__ */ jsx3(
        "button",
        {
          type: "button",
          onClick: () => onSubmit({ actionId: action.id }),
          style: action.variant === "primary" ? primaryButtonStyle : secondaryButtonStyle,
          children: action.label
        },
        action.id
      )) }) : /* @__PURE__ */ jsx3("div", { style: buttonRowStyle, children: /* @__PURE__ */ jsx3("button", { type: "button", onClick: onCancel, style: secondaryButtonStyle, children: "Close" }) })
    ] })
  ] });
};
var interactiveToolRegistry = {
  confirm_action: ConfirmActionTool,
  render_form: RenderFormTool,
  render_card: RenderCardTool
};
export {
  ChatProvider,
  ConfirmActionTool,
  DEFAULT_FRONTEND_ACTION_ALLOWLIST,
  RenderCardTool,
  RenderFormTool,
  createFrontendActions,
  defineHooks,
  fileMetadataCache,
  getCachedFileMetadata,
  interactiveToolRegistry,
  normalizeFrontendActionsArgs,
  prefetchFileMetadata,
  runFrontendActions,
  useAvailableModels,
  useChat,
  useChatClient,
  useChatConfig,
  useComments,
  useContextUsage,
  useCostDetails,
  useDeltaSync,
  useFileMetadata,
  useFiles,
  useGenerativeUI,
  useHeroSuggestions,
  useKBIntegration,
  useMessageUI,
  useNextPromptSuggestions,
  useRegistry,
  useSessionActivation,
  useSessionDraft,
  useThreads
};
